/**
 * Sitenin bağlantı haritası — TEK kaynak.
 *
 * ---------------------------------------------------------------------------
 * Neden böyle bir dosya var
 * ---------------------------------------------------------------------------
 * Şu an anasayfa, /login ve /register yayında. Footer'ın
 * gösterdiği on beş
 * sayfanın hiçbiri henüz açılmadı. Bu sayfalara `#` ya da gerçek görünen bir
 * adres vermek, kullanıcının tıklayıp hiçbir şey olmadığını (ya da 404
 * gördüğünü) yaşaması demek — site bozuk sanılır. Arama motoru için de
 * zararlı: var olmayan adrese giden bağlantı taranıp ölü bulunuyor.
 *
 * Bu yüzden adres ile "sayfa gerçekten var mı" bilgisi yan yana duruyor.
 * `live: false` olan kayıt bağlantı olarak BASILMAZ; footer onu tıklanamayan
 * düz metin + "yakında" etiketiyle gösterir. Sayfa yayına girdiğinde tek
 * yapılacak iş buradaki bayrağı `true` çevirmek — bileşene dokunulmaz.
 *
 * ---------------------------------------------------------------------------
 * Kullanım kuralları
 * ---------------------------------------------------------------------------
 * • Adresler dil öneksiz yazılır. Bağlantıyı `@/i18n/navigation`'daki `Link`
 *   basıyor, /tr önekini o ekliyor.
 * • Yeni bir sayfa eklerken önce buraya kayıt açılır, sonra bileşende
 *   anahtarıyla çağrılır. Bileşene ham adres yazılmaz.
 *
 * NOT (9 Ağu 2026): `site-header.tsx`, `cta.tsx` ve `hero.tsx` bu haritayı
 * henüz kullanmıyor; adreslerini gömüyorlar ve açılmamış sayfalara gerçek
 * bağlantı veriyorlar. Onların da buraya bağlanması ayrı bir iş olarak
 * duruyor — bu dosya o iş yapılırken genişletilecek yer.
 *
 * 17 Eyl 2026: o gömülü adreslerden /pricing artık gerçekten var. Geriye
 * /features/* kaldı; header'ın mega menüsü dördüne de bağlantı basıyor.
 *
 * Auth ekranları (giriş / kayıt / şifre sıfırlama) bağlandı.
 *
 * 17 Eyl 2026: dört yasal sayfa da açıldı ve buradaki istisna kalktı. Önceki
 * sürümde şöyle yazıyordu: "yasal bağlantılar `live: false` ama bağlantı
 * OLMAK zorunda, 'yakında' düz metnine dönüşemez." Doğruydu ve rahatsız
 * ediciydi — bir kaydın hem `live: false` olup hem bağlantı basması,
 * haritanın kendi kuralını deliyordu. Artık delik yok: sayfalar gerçekten
 * var.
 */

export type SiteLink = {
  /** Dil öneksiz adres. Önek `Link` tarafından ekleniyor. */
  href: string;
  /** Sayfa gerçekten var mı? `false` → bağlantı basılmaz, düz metin olur. */
  live: boolean;
};

export const siteLinks = {
  /* --- Yayında --- */
  home: { href: '/', live: true },
  login: { href: '/login', live: true },
  register: { href: '/register', live: true },
  /* Postadaki giriş bağlantısının vardığı ekran (10 Eki 2026). Haritada
     duruyor çünkü sayfa gerçekten var; ama kimse buraya BAĞLANMIYOR — adresi
     e-posta taşıyor ve jetonsuz açıldığında ekran "bağlantı eksik" diyor.
     `/forgot-password` ve `/reset-password` parolayla birlikte kalktı; iki
     adres de girişe yönlendiriyor. */
  authLink: { href: '/auth/link', live: true },

  /* --- Ürün: dört modül. Adresler header'ın mega menüsüyle aynı. ---
     14 Eki 2026: dördü de yayında. `featureWorkflow` → `featureAutopilot`
     (kural ekranları kalktı, yerini AI operatör aldı), `featureAnalyze` →
     `featureCampaigns` (panelin Analiz bölümü kalktı). Eski iki adres hiç
     yayına girmemişti. */
  featureAgent: { href: '/features/agent', live: true },
  featureAutopilot: { href: '/features/autopilot', live: true },
  featureCreative: { href: '/features/creative', live: true },
  featureCampaigns: { href: '/features/campaigns', live: true },

  /* --- Kaynaklar ---
     `blog` 12 Ağu 2026'da dizin olarak yayına girdi; yazı sayfaları
     (`/blog/<slug>`) 13 Ağu 2026'da açıldı ve kartlar artık bağlantı.
     Yazının kendisi bu haritada YOK ve olmayacak: adresler veritabanındaki
     slug'lardan doğuyor, burada listelenseydi her yeni yazı bir kod
     değişikliği isterdi. Bu harita sayfaları anlatıyor, içeriği değil. */
  blog: { href: '/blog', live: true },
  /* 17 Eyl 2026'da yayına girdi (app/[locale]/changelog/page.tsx). Kayıtlar
     veritabanında ve `/backstage/changelog` ekranından yönetiliyor — yani
     yeni bir sürüm duyurusu kod değişikliği gerektirmiyor.

     Yasal sayfaların aksine burada ÜÇ YERDE yazılan bir adres listesi YOK:
     kayıtlar tek bir sayfada çıpalı duruyor (`/changelog#<slug>`), kendi
     sayfaları yok. Bu harita sayfaları anlatıyor, içeriği değil — blogdaki
     yazılarla aynı ayrım. */
  changelog: { href: '/changelog', live: true },
  /* 🔴 `docs` KAYDI SİLİNDİ (18 Eyl 2026, kullanıcı kararı) — "Dokümantasyon"
     ve "Yardım merkezi" tek sayfada birleşti. İkisi de aynı soruya cevap
     veriyordu ("bu sistem nasıl kullanılıyor") ve ayrı durmalarının tek
     sonucu, cevabı arayana iki kapı gösterip hangisinden gireceğini
     kendisine sordurmaktı. Birleşen sayfa aşağıdaki `help`; footer'daki
     doküman satırı da onunla birlikte kaldırıldı.

     GELİŞTİRİCİ dokümanı (apidocs.mentorivo.com) bu birleşmeye girmedi ve
     girmemeli: o "nasıl kullanılır" değil "nasıl entegre edilir" diyor,
     okuyucusu başka biri ve ayrı bir alan adında — bu harita dil öneksiz İÇ
     adresleri tutuyor, oraya giden bağlantı düz `<a>` olmak zorunda.

     17 Eyl 2026'da yayına giren yasal sayfalarla aynı desen: sayfa gerçekten
     var (app/[locale]/help/page.tsx), bayrak `true`. */
  help: { href: '/help', live: true },

  /* --- Şirket ---
     `about` 10 Ağu 2026'da yayına girdi (app/[locale]/about/page.tsx); footer
     artık onu "yakında" düz metni yerine gerçek bağlantı olarak basıyor. */
  about: { href: '/about', live: true },
  /* 17 Eyl 2026'da yayına girdi (app/[locale]/contact/page.tsx). Form
     `POST /sales/demo-requests`e gidiyor; gelen satırlar yönetim panelindeki
     "gelen talepler" ekranında okunuyor. Fiyat sayfasındaki Enterprise
     butonunun 404'ü de böylece kapandı. */
  contact: { href: '/contact', live: true },
  /* 17 Eyl 2026'da yayına girdi (app/[locale]/pricing/page.tsx). Header, hero
     ve CTA aylardır bu adrese gerçek bağlantı veriyordu; bayrak açılana kadar
     yalnızca footer "yakında" düz metni basıyordu ve site kendi kendisiyle
     çelişiyordu. Artık üçü de aynı şeyi söylüyor. */
  pricing: { href: '/pricing', live: true },

  /* --- Yasal ---
     Adresler düz (/privacy, /terms) — /legal/* değil. Sebep: login formu
     zaten bu iki adrese bağlanıyor (components/login-form.tsx), iki ayrı
     yol düzeni tutmanın anlamı yok. `dataProtection` KVKK ve GDPR'ı tek
     sayfada topluyor; slug dil bağımsız olsun diye "data-protection".

     🔴 İlk dördü 17 Eyl 2026'da, satış belgeleri (ön bilgilendirme formu +
     mesafeli satış sözleşmesi) 22 Eyl 2026'da yayına girdi — ikincisi doğrudan
     ödeme altyapısıyla birlikte, ödeme ekranındaki onay kutusu bu iki adrese
     bağlanıyor. Adresler ARTIK ÜÇ YERDE birden
     yazılı ve üçü aynı kalmak zorunda:
       • burası (footer bu haritadan okuyor)
       • `packages/shared/src/legal.ts` → `LEGAL_DOC_SLUGS` (API bu listenin
         dışındaki slug'ı hiç sorgulamıyor)
       • `app/[locale]/<slug>/page.tsx` (dört sabit sayfa dosyası)
     Biri değişip ötekiler değişmezse sonuç 404; bu yüzden yeni bir yasal
     belge eklemek kod değişikliği ve panelden yapılamıyor (gerekçe
     `apps/api/src/legal/admin-legal.service.ts` başında). */
  privacy: { href: '/privacy', live: true },
  terms: { href: '/terms', live: true },
  /* Satış belgeleri. Slug'lar İNGİLİZCE ve dil bağımsız — "mesafeli-satis"
     yazılsaydı /en altında da Türkçe bir adres kalırdı. */
  preInformation: { href: '/pre-information', live: true },
  distanceSales: { href: '/distance-sales', live: true },
  /* 14 Eki 2026 — iyzico üye iş yeri incelemesi "Teslimat ve İade Şartları"nı
     ayrı sayfa olarak istiyor. Yeni taahhüt yok: iki satış belgesinin ifa,
     iptal ve iade hükümlerinin sade özeti (gerekçe `legal-seed-sales.ts`). */
  deliveryReturns: { href: '/delivery-returns', live: true },
  dataProtection: { href: '/data-protection', live: true },
  cookies: { href: '/cookies', live: true },
} satisfies Record<string, SiteLink>;

export type SiteLinkKey = keyof typeof siteLinks;
